/** @description 左侧流程图 组件 */
import React, {
  useEffect,
  useImperativeHandle,
  useState,
  useMemo,
  forwardRef,
} from 'react';
import ReactFlow, {
  addEdge,
  MiniMap,
  Controls,
  ControlButton,
  Background,
  useNodesState,
  useEdgesState,
  useKeyPress,
  MarkerType,
  ReactFlowInstance,
  Node,
  Edge,
  Connection,
  EdgeChange,
} from 'react-flow-renderer';
import _ from 'lodash';
import moment from 'moment';
import { useDispatch, useSelector } from 'umi';
import { useDebounceFn } from 'ahooks';
import { message } from 'antd';
import { EyeFilled, DownloadOutlined } from '@ant-design/icons';
import {
  NodeSelectItem,
  ModelDeleteEdge,
  TableData,
  DataType,
} from '@/components/index';
import Download from '@/components/download';

import MyNode from '@/components/node-default';
import GlobalNode from '@/components/node-global';
import StartNode from '@/components/node-start';

const nodeTypes = {
  defaultNode: MyNode,
  globalNode: GlobalNode,
  selectorNode: MyNode,
  startNode: StartNode,
};

import EdgeDefault from '@/components/edge-default';

const edgeTypes = {
  defaultEdge: EdgeDefault,
};

const initialNodes: Node[] = [];
const initialEdges: Edge[] = [];

type Props = {
  showNodeDetail: (node: Node) => void;
  showEdgeDetail: (edge: Edge) => void;
  showJSONDrawer: (json: any, type: 'json' | 'nAndE') => void;
  [key: string]: any;
};

const ReactFlowView: React.FC<Props> = forwardRef((props, ref) => {
  useImperativeHandle(ref, () => {
    return {
      updateEdge,
      addNode,
      setNode,
      setEdge,
      showJSON,
      nodes,
      edges,
    };
  });

  const [nodes, setNodes, onNodesChange] = useNodesState<Node[]>(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge[]>(initialEdges);

  const [isMouseEnter, setIsMouseEnter] = useState(false);
  const copyPressed = useKeyPress([
    'Meta+c',
    'ControlLeft+c',
    'ControlRight+c',
  ]);

  /** dva 状态管理 */
  const createNodeInfo = useSelector((state: any) => state.open.createNodeInfo);
  const deleteEdge = useSelector((state: any) => state.open.deleteEdge);
  const workflowConfig = useSelector((state: any) => state.open.workflowConfig);
  const tableData: TableData = useSelector(
    (state: any) => state.open.tableData,
  );
  const nodeHidden = useSelector((state: any) => state.open.nodeHidden);
  const dispatch = useDispatch();
  const nodeHiddenMap = useSelector((state: any) => state.open.nodeHiddenMap);
  const selectedNode = useSelector((state: any) => state.open.selectedNode);

  /** @description 复制节点 鼠标在画布内 */
  useEffect(() => {
    if (copyPressed && isMouseEnter) {
      const _n = _.find(nodes, (n: Node) => n.selected === true);
      if (!_n) return;
      const n = _.cloneDeep(_n);
      if (n.type === 'startNode')
        return message.warning('startNode节点不能复制！');
      let key: string = 'n-' + moment(new Date()).valueOf().toString();
      let position = { x: n.position.x + 40, y: n.position.y + 40 };
      let data = {
        ...n.data,
        id: key,
        label: `${(n.data as any).label} 复制`,
        condition: [],
      };
      let copy_node = {
        ...n,
        data,
        id: key,
        selected: false,
        position,
        positionAbsolute: position,
      };

      dispatch({
        type: 'open/addNodeHidden',
        payload: {
          key,
          value: [false],
        },
      });

      setNode(copy_node);
      message.success('复制成功');
    }
  }, [copyPressed]);

  useEffect(() => handleEdgesChange, [edges]);

  const { run: handleEdgesChange } = useDebounceFn(
    () => {
      dispatch({
        type: 'open/handleEdgesChange',
        payload: { edges },
      });
    },
    { wait: 200 },
  );

  /** 防抖 处理nodes 变化 */
  useEffect(() => run, [nodes]);

  /** @description 展开所有收起的节点 */
  const showAllHiddenItem = () => {
    nodes.forEach((n: Node) => {
      nodeHiddenMap[n.id] = [false];
    });
    dispatch({
      type: 'open/initNodeHidden',
      payload: nodeHiddenMap,
    });
    if (!reactFlowInstance) return;
    reactFlowInstance.setNodes(
      nodes.map((n: Node) => {
        return { ...n, hidden: false };
      }),
    );
    reactFlowInstance.setEdges(
      edges.map((e: Edge) => {
        return { ...e, hidden: false };
      }),
    );
  };

  /** @description 获取节点对应的nodeHiddenMap数据 */
  const getNodeHiddenValue = (id: string) => {
    return nodeHiddenMap[id].slice(-1)[0];
  };

  /** @description 根据父节点的展示情况判断是否展示子节点 */
  const isShowChildNode = (_e: Edge[]) => {
    let isShow = false;
    _e.forEach((e: Edge) => {
      const _n = _.find(nodes, (o) => o.id === e.source);
      if (!_n?.hidden) isShow = true;
    });
    return isShow;
  };

  /** @description 节点有几个未收起的父节点 */
  const getParentNodesWithNotHidden = (target: string) => {
    return _.filter(edges, (e) => e.target === target && !e.hidden);
  };

  /** @description 关闭父节点功能的子节点时 */
  const isParentAllHidden = (target: string, willHiddenNodes: string[]) => {
    const _n = _.filter(edges, (e) => e.target === target).map(
      (e: Edge) => e.source,
    );
    const diff = _.difference(_n, willHiddenNodes);
    let isAllNodesAlreadyHidden = true;

    diff.forEach((n) => {
      let _e = _.find(edges, (e) => e.source === n && e.target === target);

      // 父节点收起 或 父节点与子节点的edge收起
      if (!_e?.hidden) {
        if (!nodeHiddenMap[n].slice(-1)[0]) isAllNodesAlreadyHidden = false;
      }
    });
    return diff.length === 0 || isAllNodesAlreadyHidden;
  };

  /** @description 获取以该节点id为source 的edge 及 node */
  const getRelationNodesAndEdges = (
    source: string,
    needHiddenNode: string[],
  ) => {
    let nodeArr: string[] = [],
      edgeArr: string[] = [];
    for (let i = 0; i < edges.length; i++) {
      let e: Edge = edges[i];
      if (e.source === source) {
        let hidden = getNodeHiddenValue(source);
        if (hidden && source !== nodeHidden.nodeId) continue;
        edgeArr.push(e.id);
        const parentNodes = getParentNodesWithNotHidden(e.target);

        if (isParentAllHidden(e.target, needHiddenNode)) {
          nodeArr.push(e.target);
          continue;
        }
        // 单个关闭时有效 关闭功能子节点时无效
        if (parentNodes.length !== 1 && isShowChildNode(parentNodes)) continue;
        nodeArr.push(e.target);
      }
    }
    return [nodeArr, edgeArr];
  };

  /** @description 筛选出选中的node 的id */
  useEffect(() => {
    const _selectedNode = nodes.find((n) => n.selected);
    if (_selectedNode !== selectedNode) {
      dispatch({
        type: 'open/handleSelected',
        payload: {
          selectedNode: _selectedNode?.id,
        },
      });
    }
  }, [nodes]);

  /** @description 筛选出选中的edge 的id */
  useEffect(() => {
    const _selectedEdge = edges.find((e) => e.selected);
    dispatch({
      type: 'open/handleSelected',
      payload: {
        selectedEdge: _selectedEdge?.id,
      },
    });
  }, [edges]);

  /** @description 子节点展示切换 */
  useEffect(() => {
    if (nodeHidden.nodeId) {
      let nodeArr: string[] = [];
      let edgeArr: string[] = [];
      let loop = true;
      let sources = [nodeHidden.nodeId];

      while (loop) {
        let _sources: string[] = [];
        sources.forEach((s) => {
          const [n, e] = getRelationNodesAndEdges(s, nodeArr);
          nodeArr = Array.from(new Set([...nodeArr, ...n]));
          edgeArr = Array.from(new Set([...edgeArr, ...e]));
          _sources = [..._sources, ...n];
        });
        if (_sources.length === 0) {
          loop = false;
        } else {
          sources = _sources;
        }
      }

      const update_nodes: Node[] = [];
      let nodeAt: number[] = [];

      nodeArr.forEach((id: string) => {
        let index = _.findIndex(nodes, (o) => o.id === id);
        let hidden = nodeHidden.hidden;
        if (index > -1) {
          nodeAt.push(index);
          update_nodes.push({ ...nodes[index], hidden });
        }
      });

      const _nodes: Node[] = JSON.parse(JSON.stringify(nodes));
      _.pullAt(_nodes, nodeAt);
      const newNodes = [..._nodes, ...update_nodes];

      const update_edges: Edge[] = [];
      let edgeAt: number[] = [];
      edgeArr.forEach((id: string) => {
        let index = _.findIndex(edges, (o) => o.id === id);
        if (index > -1) {
          edgeAt.push(index);
          update_edges.push({ ...edges[index], hidden: nodeHidden.hidden });
        }
      });
      const _edges: Edge[] = JSON.parse(JSON.stringify(edges));
      _.pullAt(_edges, edgeAt);
      const newEdges = [..._edges, ...update_edges];

      reactFlowInstance?.setNodes(newNodes);
      reactFlowInstance?.setEdges(newEdges);
    }
  }, [nodeHidden]);

  /** @description 初始化 数据 */
  useEffect(() => {
    if (!workflowConfig) return;
    setNodes(workflowConfig.nodes);
    setEdges(
      workflowConfig.edges.map((e: Edge) => {
        return { ...e, type: 'defaultEdge' };
      }),
    );
  }, [workflowConfig]);

  useEffect(() => handleTableDataChange, [tableData]);

  const { run: handleTableDataChange } = useDebounceFn(
    () => {
      const newNodes: Node[] = nodes.map((n: Node) => {
        let _old = n.data?.onReceive ?? null;
        let _new = tableData[n.id]?.onReceive ?? null;
        let _old_isOnReceive = n.data?.isOnReceive as boolean;
        let _new_isOnReceive = tableData[n.id]?.isOnReceive;
        if (_old !== _new) {
          handleEdgeByNodeOnReceive(_old, _new, n.id);
        } else if (_old_isOnReceive !== _new_isOnReceive) {
          deleteSectionWhenIsOnReceiveChange(
            _old_isOnReceive,
            _new_isOnReceive,
            n.id,
          );
        }
        return {
          ...n,
          data: tableData[n.id] ?? n.data,
        };
      });
      setNodes(newNodes);
    },
    { wait: 200 },
  );

  /** edges 删除 */
  useEffect(() => {
    if (!deleteEdge.length || !reactFlowInstance || !dispatch) return;
    const oldEdges = [...edges];
    let pullList: number[] = [];

    deleteEdge.forEach((item: ModelDeleteEdge) => {
      let index = _.findIndex(edges, function (o) {
        return o.target === item.target && o.source === item.source;
      });
      if (index > -1) pullList.push(index);
    });
    if (pullList.length) _.pullAt(oldEdges, pullList);

    reactFlowInstance.setEdges(oldEdges);
    dispatch({ type: 'open/initState' });
  }, [deleteEdge]);

  /** @description 监听createNodeInfo, 创建 edge */
  useEffect(() => {
    if (!createNodeInfo.key) return;
    const _node = _.find(nodes, (n: Node) => n.id === createNodeInfo.key);

    let target_position; // 节点生成的位置
    if (createNodeInfo.source) {
      const source_node = _.find(
        nodes,
        (n: Node) => n.id === createNodeInfo.source,
      );
      let source_position = source_node?.position;
      if (source_position)
        target_position = {
          x: source_position?.x + 100,
          y: source_position?.y + 100,
        };
    }
    // 不存在此节点 有上一节点
    if (!_node && target_position)
      addNode(createNodeInfo.type, createNodeInfo.key, target_position);
    // 不存在此节点 无上一节点
    if (!_node && !target_position)
      addNode(createNodeInfo.type, createNodeInfo.key);
    if (!createNodeInfo.source) return;
    setTimeout(() => {
      setEdge({
        id: 'e-' + moment(new Date()).valueOf().toString(),
        source: createNodeInfo.source,
        target: createNodeInfo.key,
        type: 'defaultEdge',
        markerEnd: { type: MarkerType.ArrowClosed },
        label: createNodeInfo.condition,
      });
      if (!dispatch) return;
      dispatch({ type: 'open/initState' });
    });
  }, [createNodeInfo.key, createNodeInfo.source]);

  const { run } = useDebounceFn(
    () => {
      let arr: NodeSelectItem[] = [];
      nodes.forEach((n: Node) => {
        arr.push({
          label: n.data.label,
          key: n.id,
          type: n.type,
          hidden: Boolean(n?.hidden),
        });
      });
      if (!dispatch) {
        return;
      }
      dispatch({
        type: 'open/setNodeSelectList',
        payload: {
          data: arr,
        },
      });
    },
    { wait: 100 },
  );

  const onConnect = (params: Connection) => {
    const node = getNodeById(params.source ?? '');
    if (getNodeHiddenValue(node.id))
      return message.warning('请先将父节点状态切换为展开！');

    const execute =
      node.data?.isOnReceive &&
      node.data?.onReceive &&
      node.data?.onReceive.length
        ? node.data?.onReceive[0]?.code
        : '';
    if (execute === '匹配字段')
      return message.warning('该节点执行匹配字段规则！');
    const newEdges = _.filter(edges, (e) => e.source !== params.source);

    setEdges((eds) => {
      const p = {
        ...params,
        type: 'defaultEdge',
        markerEnd: { type: MarkerType.ArrowClosed },
      };
      return addEdge(p, newEdges);
    });
  };

  const [reactFlowInstance, setReactFlowInstance] =
    useState<ReactFlowInstance | null>(null);

  const onInit = (reactFlowInstance: ReactFlowInstance) => {
    setReactFlowInstance(reactFlowInstance);
    console.log('flow loaded:', reactFlowInstance);
  };

  /**  @description 根据node的匹配信息 处理该以该node为source的edge */
  const handleEdgeByNodeOnReceive = (
    _old: null | DataType[],
    _new: null | DataType[],
    nodeId: string,
  ) => {
    if ((!_old || _old.length === 0) && (!_new || _new.length === 0)) {
      // 清除source 为 该 node 的 edge
    } else if ((!_old || _old.length === 0) && _new) {
      // _new 存在
      let execute = _new[0].code ?? '';
      deleteSection(execute);
    } else if ((!_new || _new.length === 0) && _old) {
      // _old 存在
      let execute = _old[0].code;
      deleteAll();
    } else if (_old && _new) {
      // _old _new 都存在
      let execute = _new[0].code ?? '';
      deleteSection(execute);
    }

    // 删除一部分
    function deleteSection(execute: string) {
      const newEdges = _.filter(edges, function (e) {
        if (e.source !== nodeId) return true;
        if (execute === '匹配字段') {
          return Boolean(e.label);
        }
        if (execute !== '匹配字段' && e.label && dispatch) {
          dispatch({
            type: 'open/setDeleteEdgeTarget',
            payload: [{ source: nodeId, target: e.target }],
          });
        }
        return !e.label;
      });
      realSetEdge(newEdges);
    }
    // 删除全部
    function deleteAll() {
      const newEdges = _.filter(edges, (e) => e.source !== nodeId);
      realSetEdge(newEdges);
    }
  };

  // 节点切换匹配开关时，相关edge的delete
  function deleteSectionWhenIsOnReceiveChange(
    old_is: boolean,
    new_id: boolean,
    nodeId: string,
  ) {
    if (!old_is && new_id) {
      // 匹配 开启
      const newEdges = _.filter(edges, function (e) {
        if (e.source !== nodeId) return true;
        return Boolean(e.label);
      });
      realSetEdge(newEdges);
    }
  }

  /** @description 创建节点 */
  const addNode = (
    type: string,
    key: string = 'n-' + moment(new Date()).valueOf().toString(),
    position = { x: 200, y: -200 },
  ) => {
    if (!reactFlowInstance) {
      return;
    }

    const isExistStart = _.findIndex(nodes, function (o: Node) {
      return o.id === 'introduction';
    });

    if (type === 'startNode' && isExistStart > -1)
      return message.warning('只能有一个startNode节点！');

    const id = type === 'startNode' ? 'introduction' : key;

    dispatch({
      type: 'open/addNodeHidden',
      payload: {
        key: id,
        value: [false],
      },
    });

    reactFlowInstance.addNodes({
      id,
      data: { label: `Q ${id}`, id },
      type,
      position,
    });
  };

  /** @description 更新节点 */
  const setNode = (node: Node) => {
    if (!reactFlowInstance || !node) {
      return;
    }

    const newNodes = [
      ..._.remove(nodes, function (n) {
        return n.id !== node.id;
      }),
      node,
    ];
    reactFlowInstance.setNodes(newNodes);
  };

  /** @description 更新edges label */
  const updateEdge = (
    preLabel: string,
    nextLabel: string,
    sourceNodeId: string,
  ) => {
    let edge = _.find(
      edges,
      (e) => e.source === sourceNodeId && e.label === preLabel,
    );
    if (!edge) return;
    edge.label = nextLabel;
    setEdge(edge);
  };

  /** @description 创建或更新edges */
  const setEdge = (edge: Edge) => {
    const node = getNodeById(edge.source);
    if (!node) return;
    const execute =
      node.data?.onReceive && node.data?.onReceive.length
        ? node.data?.onReceive[0]?.code
        : '';
    const newEdges = [
      ..._.filter(edges, function (e) {
        if (e.source !== edge.source) return true;
        if (e.id === edge.id) return false;
        if (execute === '匹配字段') {
          return Boolean(e.label);
        }
        return !e.label;
      }),
      edge,
    ];
    realSetEdge(newEdges);
  };

  const realSetEdge = (newEdges: Edge[]) => {
    if (!reactFlowInstance) {
      return;
    }
    reactFlowInstance.setEdges(newEdges);
  };

  const getNodeById = (id: string): Node => {
    return _.head(
      _.filter(nodes, function (n: Node) {
        if (n.id === id) return n;
      }),
    ) as Node;
  };

  const showJSON = (type: 'json' | 'nAndE' = 'json') => {
    if (type === 'json') {
      showProcessJSON();
    } else {
      props.showJSONDrawer(
        {
          nodes,
          edges,
        },
        'nAndE',
      );
    }
  };

  const showProcessJSON = () => {
    if (!nodes.length) {
      props.showJSONDrawer(null, 'json');
      return;
    }

    props.showJSONDrawer({ nodes, edges }, 'json');
  };

  /** @description edge被删除 */
  const onEdgesDelete = (_edges: Edge[]) => {
    const arr = _edges.map((e: Edge) => {
      return { source: e.source, target: e.target };
    });
    if (!dispatch) return;
    dispatch({ type: 'open/setDeleteEdgeTarget', payload: arr });
  };

  return (
    <div style={{ width: '100%', height: 'calc(100vh - 48px)' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onMouseEnter={() => setIsMouseEnter(true)}
        onMouseLeave={() => setIsMouseEnter(false)}
        onNodesChange={onNodesChange}
        onEdgesChange={(edgeChanges) => {
          onEdgesChange(edgeChanges);
          const _change: Edge[] = [];
          edgeChanges.forEach((vo: any) => {
            if (vo.item) _change.push(vo.item);
          });
          if (_change.length === 0) return;
          if (edges.length > _change.length) {
            const diff = _.differenceBy(edges, _change, 'id');
            if (!diff.length) return;
            onEdgesDelete(diff);
          }
        }}
        onConnect={onConnect}
        onInit={onInit}
        onNodeClick={(e, node) => {
          props.showNodeDetail(node);
        }}
        onEdgeClick={(e, edge) => {
          props.showEdgeDetail(edge);
        }}
        onEdgesDelete={onEdgesDelete}
        fitView
        attributionPosition="top-right"
        defaultZoom={0.5}
      >
        <MiniMap
          nodeStrokeColor={(n: Node) => {
            if (n.style?.background) return n.style.background + '';
            if (n.type === 'input') return '#0041d0';
            if (n.type === 'output') return '#ff0072';
            if (n.type === 'default') return '#1a192b';

            return '#eee';
          }}
          nodeColor={(n: Node) => {
            if (n.style?.background) return n.style.background + '';

            return '#fff';
          }}
          nodeBorderRadius={2}
        />
        <Controls>
          <ControlButton onClick={showAllHiddenItem}>
            <EyeFilled />
          </ControlButton>
          <ControlButton onClick={() => Download(reactFlowInstance?.getNodes)}>
            <DownloadOutlined />
          </ControlButton>
        </Controls>
        <Background color="#aaa" gap={16} />
      </ReactFlow>
    </div>
  );
});

export default ReactFlowView;
